// src/store/reducers/noteReducer.ts
import type { InterfaceApiNote } from '@interfaces/InterfaceApiNote.ts';
import type { InterfaceGenericMap } from '@interfaces/InterfaceGenericMap.ts';
// Import new Notes API types
import type {
  CreateNoteRequest,
  NoteableTypeResource,
  NoteHistory,
  NoteResource,
  NoteTypeResource,
  UpdateNoteRequest,
} from '@interfaces/notes/notes.types';
import type { PaginatedResponse } from '@interfaces/shared/common.types';
import type { NoteableType, NoteType } from '@interfaces/shared/enums';
import axiosClientCached from '@lib/axiosClientCached.ts'; // For custom thunk
import { processStoreError } from '@lib/errorHandler'; // For custom thunk
import {
  createAsyncThunk,
  createSelector,
  createSlice,
  type Draft,
  type PayloadAction,
  type SerializedError,
} from '@reduxjs/toolkit';
import type { AppState } from '@store/appStore.ts';
import { meLogOut, selectCurrentCompanySlug } from '@store/reducers/meReducer.ts';
import createDebouncedAsyncThunk from '@store/reducers/reducerHelpers/createDebouncedAsyncThunk.ts';
import thunkDelete, { type InterfaceThunkDeleteData } from '@store/reducers/reducerHelpers/thunkDelete.ts';
import { thunkGet } from '@store/reducers/reducerHelpers/thunkGet.ts';
import { defaultTypeDebounceTimeMs } from '@store/reducers/reducerHelpers/thunkHelperShared.ts';
import { thunkPost } from '@store/reducers/reducerHelpers/thunkPost.ts';
import thunkPut from '@store/reducers/reducerHelpers/thunkPut.ts';

// Keep legacy interfaces for backward compatibility during migration
import type { InterfaceNoteableType } from '@/interfaces/InterfaceNoteableType';
import type { InterfaceNoteCategoryType } from '@/interfaces/InterfaceNoteCategoryType';

import { addSuccessNotification } from './notificationReducer';

// Remove duplicate PaginatedResponse - using the one from common.types

// Payload type for getNotesForNoteable.fulfilled
interface GetNotesForNoteableFulfilledPayload {
  data: PaginatedResponse<NoteResource>;
  entityKey: string;
  page: number;
}

// Payload type for getNotesForNoteable.rejected
interface GetNotesForNoteableRejectedPayload {
  error: SerializedError;
  entityKey: string;
}

export interface NoteReducerState {
  // New API types
  noteableTypes: InterfaceGenericMap<NoteableTypeResource>;
  noteTypes: InterfaceGenericMap<NoteTypeResource>;
  notes: InterfaceGenericMap<NoteResource>;
  noteHistory: InterfaceGenericMap<NoteHistory[]>;

  // Legacy types (for backward compatibility during migration)
  legacyNoteableTypes: InterfaceGenericMap<InterfaceNoteableType>;
  legacyNoteCategories: InterfaceGenericMap<InterfaceNoteCategoryType>;
  entityNotes: {
    [entityKey: string]: {
      noteUuids: string[];
      pagination: {
        currentPage: number;
        lastPage: number;
        perPage: number;
        totalItems: number;
      } | null;
      loading: boolean;
      error: SerializedError | null;
    };
  };
  noteableTypesStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  noteableTypesError: SerializedError | null;
  noteCategoriesStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  noteCategoriesError: SerializedError | null;
  mutationStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  mutationError: SerializedError | null;
}

const initialState: NoteReducerState = {
  // New API types
  noteableTypes: {},
  noteTypes: {},
  notes: {},
  noteHistory: {},

  // Legacy types
  legacyNoteableTypes: {},
  legacyNoteCategories: {},

  entityNotes: {},
  noteableTypesStatus: 'idle',
  noteableTypesError: null,
  noteCategoriesStatus: 'idle',
  noteCategoriesError: null,
  mutationStatus: 'idle',
  mutationError: null,
};

export const sliceName = 'note';

// Convert API note format to NoteResource format
const convertApiNoteToResource = (apiNote: InterfaceApiNote): NoteResource => ({
  uuid: apiNote.uuid,
  id: undefined,
  noteTypeSlug: apiNote.note_type_slug as NoteType,
  noteableType: apiNote.noteable_type_slug as NoteableType,
  noteableUuid: apiNote.noteable_uuid,
  parentNoteUuid: apiNote.parent_note_uuid || undefined,
  noteData: {
    subject: apiNote.note_data.subject,
    content: apiNote.note_data.content,
    metadata: undefined,
  },
  creatorContext: {
    userUuid: apiNote.user_uuid,
    userName: apiNote.user_name || '',
    userType: 'user',
  },
  createdAt: apiNote.created_at,
  updatedAt: apiNote.updated_at,
  deletedAt: apiNote.deleted_at || undefined,
  replies: apiNote.replies?.map(convertApiNoteToResource),
});

const normalizeAndStoreApiNotes = (
  state: Draft<NoteReducerState>,
  notesToProcess: (NoteResource | InterfaceApiNote)[],
) => {
  const newNoteUuids: string[] = [];
  // Convert all notes to NoteResource format
  const convertedNotes = notesToProcess.map((note) =>
    'note_type_slug' in note ? convertApiNoteToResource(note) : note,
  );
  const queue: NoteResource[] = [...convertedNotes];
  while (queue.length > 0) {
    const note = queue.shift();
    if (note) {
      newNoteUuids.push(note.uuid);

      const { replies: _replies, ...restOfNote } = note;
      state.notes[note.uuid] = restOfNote;
      if (note.replies && note.replies.length > 0) {
        queue.push(...note.replies);
      }
    }
  }
  return newNoteUuids;
};

export const noteSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    clearEntityNotes: (state, action: PayloadAction<{ entityKey: string }>) => {
      if (state.entityNotes[action.payload.entityKey]) {
        delete state.entityNotes[action.payload.entityKey];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(meLogOut.fulfilled, () => initialState)

      // New API Thunks
      // Get Noteable Types (New)
      .addCase(getNoteableTypesNew.fulfilled, (state, action: PayloadAction<NoteableTypeResource[]>) => {
        state.noteableTypesStatus = 'succeeded';
        state.noteableTypes = {};
        if (Array.isArray(action.payload)) {
          action.payload.forEach((type) => {
            state.noteableTypes[type.slug] = type;
          });
        }
      })

      // Get Note Types (New)
      .addCase(getNoteTypesNew.fulfilled, (state, action: PayloadAction<NoteTypeResource[]>) => {
        state.noteCategoriesStatus = 'succeeded';
        state.noteTypes = {};
        if (Array.isArray(action.payload)) {
          action.payload.forEach((type) => {
            state.noteTypes[type.slug] = type;
          });
        }
      })

      // Create Note (New)
      .addCase(createNoteNew.fulfilled, (state, action: PayloadAction<NoteResource>) => {
        state.mutationStatus = 'succeeded';
        normalizeAndStoreApiNotes(state, [action.payload]);
        const newNote = action.payload;
        if (!newNote.parentNoteUuid) {
          const entityKey = `${newNote.noteableType}_${newNote.noteableUuid}`;
          if (state.entityNotes[entityKey]) {
            state.entityNotes[entityKey].noteUuids = [newNote.uuid, ...state.entityNotes[entityKey].noteUuids];
            if (state.entityNotes[entityKey].pagination) {
              state.entityNotes[entityKey].pagination.totalItems += 1;
            }
          } else {
            state.entityNotes[entityKey] = {
              noteUuids: [newNote.uuid],
              pagination: null,
              loading: false,
              error: null,
            };
          }
        }
      })

      // Get Note (New)
      .addCase(getNoteNew.fulfilled, (state, action: PayloadAction<NoteResource>) => {
        state.mutationStatus = 'succeeded';
        normalizeAndStoreApiNotes(state, [action.payload]);
      })

      // Update Note (New)
      .addCase(updateNoteNew.fulfilled, (state, action: PayloadAction<NoteResource>) => {
        state.mutationStatus = 'succeeded';
        normalizeAndStoreApiNotes(state, [action.payload]);
      })

      // Delete Note (New)
      .addCase(deleteNoteNew.fulfilled, (state, action) => {
        state.mutationStatus = 'succeeded';
        const deletedNoteUuid = action.meta.arg?.urlVarsReplaceMap?.note;

        if (deletedNoteUuid && state.notes[deletedNoteUuid]) {
          state.notes[deletedNoteUuid].deletedAt = new Date().toISOString();
        }

        Object.keys(state.entityNotes).forEach((entityKey) => {
          const entityNoteInfo = state.entityNotes[entityKey];
          if (entityNoteInfo && deletedNoteUuid) {
            const initialCount = entityNoteInfo.noteUuids.length;
            entityNoteInfo.noteUuids = entityNoteInfo.noteUuids.filter((id) => id !== deletedNoteUuid);
            if (entityNoteInfo.pagination && initialCount > entityNoteInfo.noteUuids.length) {
              entityNoteInfo.pagination.totalItems -= 1;
            }
          }
        });
      })
      // Legacy getNoteableTypes (Global)
      .addCase(getNoteableTypes.pending, (state) => {
        state.noteableTypesStatus = 'loading';
      })
      .addCase(getNoteableTypes.fulfilled, (state, action: PayloadAction<InterfaceNoteableType[]>) => {
        state.noteableTypesStatus = 'succeeded';
        state.legacyNoteableTypes = {}; // Clear before populating
        action.payload.forEach((type) => {
          state.legacyNoteableTypes[type.slug] = type;
        });
      })
      .addCase(getNoteableTypes.rejected, (state, action) => {
        state.noteableTypesStatus = 'failed';
        state.noteableTypesError = action.error;
      })

      // Legacy getNoteCategories (Global)
      .addCase(getNoteCategories.pending, (state) => {
        state.noteCategoriesStatus = 'loading';
      })
      .addCase(getNoteCategories.fulfilled, (state, action: PayloadAction<InterfaceNoteCategoryType[]>) => {
        state.noteCategoriesStatus = 'succeeded';
        state.legacyNoteCategories = {}; // Clear before populating
        action.payload.forEach((type) => {
          state.legacyNoteCategories[type.slug] = type;
        });
      })
      .addCase(getNoteCategories.rejected, (state, action) => {
        state.noteCategoriesStatus = 'failed';
        state.noteCategoriesError = action.error;
      })

      // getNotesForNoteable (Company-Scoped - Custom Thunk)
      .addCase(getNotesForNoteable.pending, (state, action) => {
        const { entityKey } = action.meta.arg;
        state.entityNotes[entityKey] = {
          ...(state.entityNotes[entityKey] || { noteUuids: [], pagination: null, loading: false, error: null }),
          loading: true,
          error: null,
        };
      })
      .addCase(getNotesForNoteable.fulfilled, (state, action: PayloadAction<GetNotesForNoteableFulfilledPayload>) => {
        const { data: paginatedResponse, entityKey, page } = action.payload;
        const topLevelNoteUuids = normalizeAndStoreApiNotes(state, paginatedResponse.data);

        const currentEntityNotes = state.entityNotes[entityKey] || {
          noteUuids: [],
          pagination: null,
          loading: false,
          error: null,
        };

        state.entityNotes[entityKey] = {
          noteUuids:
            page === 1
              ? topLevelNoteUuids
              : [...currentEntityNotes.noteUuids, ...topLevelNoteUuids].filter(
                  (value, index, self) => self.indexOf(value) === index,
                ),
          pagination: {
            currentPage: paginatedResponse.meta.current_page,
            lastPage: paginatedResponse.meta.last_page,
            perPage: paginatedResponse.meta.per_page,
            totalItems: paginatedResponse.meta.total,
          },
          loading: false,
          error: null,
        };
      })
      .addCase(getNotesForNoteable.rejected, (state, action) => {
        const { payload } = action;
        const entityKey = payload?.entityKey || action.meta.arg.entityKey;
        if (entityKey) {
          state.entityNotes[entityKey] = {
            ...(state.entityNotes[entityKey] || { noteUuids: [], pagination: null, loading: false, error: null }),
            loading: false,
            error: payload?.error || action.error,
          };
        } else {
          state.mutationStatus = 'failed';
          state.mutationError = payload?.error || action.error;
        }
      })

      // getNote (Company-Scoped)
      .addCase(getNote.pending, (state) => {
        state.mutationStatus = 'loading';
      })
      .addCase(getNote.fulfilled, (state, action: PayloadAction<InterfaceApiNote>) => {
        state.mutationStatus = 'succeeded';
        normalizeAndStoreApiNotes(state, [action.payload]);
      })
      .addCase(getNote.rejected, (state, action) => {
        state.mutationStatus = 'failed';
        state.mutationError = action.error;
      })

      // createNote (Company-Scoped)
      .addCase(createNote.pending, (state) => {
        state.mutationStatus = 'loading';
        state.mutationError = null;
      })
      .addCase(createNote.fulfilled, (state, action: PayloadAction<InterfaceApiNote>) => {
        state.mutationStatus = 'succeeded';
        normalizeAndStoreApiNotes(state, [action.payload]);
        const newNote = action.payload;
        if (!newNote.parent_note_uuid) {
          const entityKey = `${newNote.noteable_type_slug}_${newNote.noteable_uuid}`;
          if (state.entityNotes[entityKey]) {
            state.entityNotes[entityKey].noteUuids = [newNote.uuid, ...state.entityNotes[entityKey].noteUuids];
            if (state.entityNotes[entityKey].pagination) {
              state.entityNotes[entityKey].pagination.totalItems += 1;
            }
          } else {
            // If entityKey doesn't exist, create it
            state.entityNotes[entityKey] = {
              noteUuids: [newNote.uuid],
              pagination: null, // No pagination info for a single new note
              loading: false,
              error: null,
            };
          }
        }
      })
      .addCase(createNote.rejected, (state, action) => {
        state.mutationStatus = 'failed';
        state.mutationError = action.error;
      })

      // updateNote (Company-Scoped)
      .addCase(updateNote.pending, (state) => {
        state.mutationStatus = 'loading';
      })
      .addCase(updateNote.fulfilled, (state, action: PayloadAction<InterfaceApiNote>) => {
        state.mutationStatus = 'succeeded';
        normalizeAndStoreApiNotes(state, [action.payload]);
      })
      .addCase(updateNote.rejected, (state, action) => {
        state.mutationStatus = 'failed';
        state.mutationError = action.error;
      })

      // deleteNote (Company-Scoped)
      .addCase(deleteNote.pending, (state) => {
        state.mutationStatus = 'loading';
      })
      .addCase(deleteNote.fulfilled, (state, action) => {
        state.mutationStatus = 'succeeded';
        // The actual payload for delete might be empty or just a success message.
        // The important part is action.meta.arg to identify which note was deleted.
        const deletedNoteUuid = (
          action.meta.arg as InterfaceThunkDeleteData<undefined, { '{companySlug}': string; '{note_uuid}': string }>
        )?.urlVarsReplaceMap?.['{note_uuid}'];

        if (deletedNoteUuid && state.notes[deletedNoteUuid]) {
          state.notes[deletedNoteUuid].deletedAt = new Date().toISOString(); // Mark as deleted
        }
        // Optionally, remove from entityNotes lists or filter out in selectors
        Object.keys(state.entityNotes).forEach((entityKey) => {
          const entityNoteInfo = state.entityNotes[entityKey];
          if (entityNoteInfo && deletedNoteUuid) {
            const initialCount = entityNoteInfo.noteUuids.length;
            entityNoteInfo.noteUuids = entityNoteInfo.noteUuids.filter((id) => id !== deletedNoteUuid);
            if (entityNoteInfo.pagination && initialCount > entityNoteInfo.noteUuids.length) {
              entityNoteInfo.pagination.totalItems -= 1;
            }
          }
        });
      })
      .addCase(deleteNote.rejected, (state, action) => {
        state.mutationStatus = 'failed';
        state.mutationError = action.error;
      });
  },
});

export const { clearEntityNotes } = noteSlice.actions;

// Thunks

// Global Thunks (No company slug) - API returns { data: [...] }
// thunkGet.get<U, R> where U is for urlVarsReplaceMap, R is ResponseType
export const getNoteableTypes = thunkGet.get(sliceName, '/type/note/noteable');
export const getNoteCategories = thunkGet.get(sliceName, '/type/note/types');

// New API Thunks for Notes
export const getNoteableTypesNew = thunkGet.get<undefined>(sliceName, '/api/v1/type/note/noteable');
export const getNoteTypesNew = thunkGet.get<undefined>(sliceName, '/api/v1/type/note/types');

// Company-Scoped Thunks

// New API thunks using new endpoints
export const createNoteNew = thunkPost.compUrl<CreateNoteRequest>(sliceName, '/api/v1/c/{companySlug}/admin/notes', [
  addSuccessNotification({ title: 'Note Created', message: 'Note successfully created.' }),
]);

export const getNoteNew = thunkGet.compUrl<{ note: string }>(sliceName, '/api/v1/c/{companySlug}/admin/notes/{note}');

export const updateNoteNew = thunkPut.compUrl<UpdateNoteRequest, { note: string }>(
  sliceName,
  '/api/v1/c/{companySlug}/admin/notes/{note}',
  [addSuccessNotification({ title: 'Note Updated', message: 'Note successfully updated.' })],
);

export const deleteNoteNew = thunkDelete.compUrl<{ note: string }>(
  sliceName,
  '/api/v1/c/{companySlug}/admin/notes/{note}',
  [addSuccessNotification({ title: 'Note Deleted', message: 'Note successfully deleted.' })],
);

// Legacy interface for backward compatibility
export interface CreateNotePayload {
  noteable_type_slug: string;
  noteable_uuid: string;
  note_type_slug: string;
  note_data: {
    content: string;
    subject?: string;
  };
  parent_note_uuid?: string | null;
}

// thunkPost.compUrl<T, U, R> where T is form, U is urlVars, R is Response
export const createNote = thunkPost.compUrl(sliceName, '/c/{companySlug}/admin/notes', [
  addSuccessNotification({ title: 'Note Created', message: 'Note successfully created.' }),
]);

export interface GetNotesForNoteableParams {
  companySlug?: string; // Optional, will use current if not provided
  noteableTypePlural: string;
  noteableUuid: string;
  entityKey: string;
  page?: number;
  per_page?: number;
  note_type?: string;
}

export const getNotesForNoteable = createAsyncThunk<
  GetNotesForNoteableFulfilledPayload, // Return type
  GetNotesForNoteableParams, // Argument type
  { rejectValue: GetNotesForNoteableRejectedPayload; state: AppState } // ThunkApiConfig
>(`${sliceName}/getForNoteable`, async (params: GetNotesForNoteableParams, { getState, rejectWithValue }) => {
  const { companySlug } = params || selectCurrentCompanySlug(getState());
  if (!companySlug) {
    return rejectWithValue({
      error: { name: 'Config Error', message: 'Company slug not found' },
      entityKey: params.entityKey,
    });
  }
  let url = `/api/v1/c/${companySlug}/admin/notes/${params.noteableTypePlural}/${params.noteableUuid}`;
  const query = new URLSearchParams();
  if (params.page) {
    query.append('page', params.page.toString());
  }
  if (params.per_page) {
    query.append('per_page', params.per_page.toString());
  }
  if (params.note_type) {
    query.append('note_type', params.note_type);
  }

  const queryString = query.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  try {
    const response = await axiosClientCached.get(url);
    return { data: response.data, entityKey: params.entityKey, page: params.page || 1 };
  } catch (error) {
    const processedError = processStoreError(error);
    return rejectWithValue({
      error: {
        name: 'API Error',
        message: processedError.message,
        code: String(processedError.statusCode),
      },
      entityKey: params.entityKey,
    });
  }
});

export interface GetNoteParams {
  note_uuid: string; // Matches API {note_uuid}
}

// thunkGet.compUrl<U, R>
export const getNote = thunkGet.compUrl(sliceName, '/c/{companySlug}/admin/notes/{note_uuid}');

export interface UpdateNotePayload {
  // note_uuid is part of URL
  note_type_slug?: string;
  note_data?: {
    content?: string;
    subject?: string;
  };
}

// thunkPut.compUrl<T, U, R>
export const updateNote = thunkPut.compUrl(sliceName, '/c/{companySlug}/admin/notes/{note_uuid}', [
  addSuccessNotification({ title: 'Note Updated', message: 'Note successfully updated.' }),
]);

export interface DeleteNoteUrlParams {
  '{companySlug}': string;
  '{note_uuid}': string;
}

// thunkDelete.compUrl<T, U, R>
// For delete, T (form data) is undefined. U is for URL params. R is response (InterfaceApiNote | undefined for soft delete).
export const deleteNote = thunkDelete.compUrl(sliceName, '/c/{companySlug}/admin/notes/{note_uuid}', [
  addSuccessNotification({ title: 'Note Deleted', message: 'Note successfully deleted.' }),
]);

// Selectors
export const selectNoteState = (state: AppState) => state.note;
export const selectAllApiNotes = (state: AppState) => selectNoteState(state).notes;

// New API selectors
export const selectNoteableTypes = (state: AppState) => selectNoteState(state).noteableTypes;
export const selectNoteableTypesArray = createSelector([selectNoteableTypes], (types) => Object.values(types));
export const selectNoteTypes = (state: AppState) => selectNoteState(state).noteTypes;
export const selectNoteTypesArray = createSelector([selectNoteTypes], (types) => Object.values(types));
export const selectNoteHistory = (state: AppState) => selectNoteState(state).noteHistory;

// Legacy selectors for backward compatibility
export const selectLegacyNoteableTypes = (state: AppState) => selectNoteState(state).legacyNoteableTypes;
export const selectLegacyNoteableTypesArray = createSelector([selectLegacyNoteableTypes], (types) =>
  Object.values(types),
);
export const selectLegacyNoteCategories = (state: AppState) => selectNoteState(state).legacyNoteCategories;
export const selectLegacyNoteCategoriesArray = createSelector([selectLegacyNoteCategories], (categories) =>
  Object.values(categories),
);

export const selectApiNoteById = (noteUuid: string | undefined) =>
  createSelector([selectAllApiNotes], (notes) => (noteUuid ? notes[noteUuid] : undefined));

export const selectTopLevelApiNotesForEntity = (entityKey: string | undefined) =>
  createSelector([selectNoteState, selectAllApiNotes], (noteState, allNotes) => {
    if (!entityKey || !noteState.entityNotes[entityKey]) {
      return [];
    }
    return noteState.entityNotes[entityKey].noteUuids
      .map((uuid) => allNotes[uuid])
      .filter((note) => note && !note.deletedAt);
  });

export const selectApiNotesWithRepliesForEntity = (entityKey: string | undefined) =>
  createSelector([selectTopLevelApiNotesForEntity(entityKey), selectAllApiNotes], (topLevelNotes, allNotes) => {
    const buildReplies = (noteId: string): NoteResource[] =>
      Object.values(allNotes)
        .filter((note) => note && note.parentNoteUuid === noteId && !note.deletedAt)
        .map((note) => ({
          ...note,
          replies: buildReplies(note.uuid),
        }))
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

    return topLevelNotes.map((note) => ({
      ...note,
      replies: buildReplies(note.uuid),
    }));
  });

export const selectEntityNotesInfo = (entityKey: string | undefined) =>
  createSelector([selectNoteState], (noteState) => {
    if (!entityKey) {
      return { loading: false, error: null, pagination: null, hasMore: false };
    }
    const entityData = noteState.entityNotes[entityKey];
    return {
      loading: entityData?.loading || false,
      error: entityData?.error || null,
      pagination: entityData?.pagination || null,
      hasMore: entityData?.pagination ? entityData.pagination.currentPage < entityData.pagination.lastPage : false,
    };
  });

export const selectNoteableTypesStatus = (state: AppState) => selectNoteState(state).noteableTypesStatus;
export const selectNoteCategoriesStatus = (state: AppState) => selectNoteState(state).noteCategoriesStatus;
export const selectNoteMutationStatus = (state: AppState) => selectNoteState(state).mutationStatus;

export default noteSlice.reducer;

export const loadInitialNoteData = createDebouncedAsyncThunk(
  `${sliceName}/initialNoteData`,
  async (_, { dispatch }) => {
    // Load both new and legacy data during migration period
    dispatch(getNoteableTypesNew());
    dispatch(getNoteTypesNew());

    // Keep legacy endpoints for backward compatibility
    dispatch(getNoteableTypes());
    dispatch(getNoteCategories());
  },
  defaultTypeDebounceTimeMs,
);
